import { useAuth0 } from "@auth0/auth0-react"
import { useCallback, useMemo } from "react"
import { httpRequest, RequestOptions } from "../api/client"
import { ApiError } from "../api/ApiError"
import { notifyError } from "../api/notify"

// Hook centralizado para todas las llamadas HTTP autenticadas del frontend.
// - Inyecta el token de Auth0 en cada request.
// - Notifica errores 401/403/network con un toast por defecto.
// - Devuelve siempre Promise<T> tipada y propaga ApiError al caller, que
//   puede capturarlo si necesita un manejo especifico.

export interface ApiCallOptions extends RequestOptions {
  showErrorToast?: boolean
  errorMessage?: string
}

export interface ApiClient {
  get: <T>(url: string, options?: ApiCallOptions) => Promise<T>
  post: <T>(url: string, body?: unknown, options?: ApiCallOptions) => Promise<T>
  put: <T>(url: string, body?: unknown, options?: ApiCallOptions) => Promise<T>
  patch: <T>(
    url: string,
    body?: unknown,
    options?: ApiCallOptions,
  ) => Promise<T>
  del: <T>(url: string, options?: ApiCallOptions) => Promise<T>
}

const defaultErrorMessage = (error: ApiError): string => {
  switch (error.kind) {
    case "network":
      return "No se pudo contactar al servidor"
    case "unauthorized":
      return "Tu sesion expiro, volve a iniciar sesion"
    case "forbidden":
      return "No tenes permisos para realizar esta accion"
    case "notFound":
      return "El recurso solicitado no existe"
    case "validation":
      return error.message || "Los datos enviados no son validos"
    case "server":
      return "Ocurrio un error en el servidor"
    default:
      return error.message || "Ha ocurrido un error"
  }
}

export const useApi = (): ApiClient => {
  const { getAccessTokenSilently } = useAuth0()

  const call = useCallback(
    async <T>(
      method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
      url: string,
      body?: unknown,
      options: ApiCallOptions = {},
    ): Promise<T> => {
      const { showErrorToast = true, errorMessage, ...rest } = options
      try {
        const token = await getAccessTokenSilently()
        return await httpRequest<T>(method, url, body, { ...rest, token })
      } catch (error) {
        if (error instanceof ApiError) {
          if (showErrorToast) {
            notifyError(errorMessage ?? defaultErrorMessage(error))
          }
          throw error
        }
        const wrapped = new ApiError(
          (error as Error)?.message ?? "Error inesperado",
          "unknown",
        )
        if (showErrorToast) {
          notifyError(errorMessage ?? defaultErrorMessage(wrapped))
        }
        throw wrapped
      }
    },
    [getAccessTokenSilently],
  )

  return useMemo<ApiClient>(
    () => ({
      get: (url, options) => call("GET", url, undefined, options),
      post: (url, body, options) => call("POST", url, body, options),
      put: (url, body, options) => call("PUT", url, body, options),
      patch: (url, body, options) => call("PATCH", url, body, options),
      del: (url, options) => call("DELETE", url, undefined, options),
    }),
    [call],
  )
}
